import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { ArrowRight, ZoomIn, ZoomOut, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './SwiperCustom.css'; // Custom styles for pagination override

import productsData from '../../data/productsData';
import ProductCard from './ProductCard';

function ProductsAndDesigns() {
  const [activeImage, setActiveImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  const openLightbox = (imgSrc, imgAlt, title) => {
    setActiveImage({ src: imgSrc, alt: imgAlt, title: title });
    setZoomScale(1);
  };

  const closeLightbox = () => {
    setActiveImage(null);
    setZoomScale(1);
  };

  return (
    <section className="relative bg-cogLight py-20 lg:py-28 overflow-hidden font-sans">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Intro */}
        <div className="flex flex-wrap justify-between items-end gap-8 mb-14 lg:mb-16">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold text-cogCyan block mb-3">
              Products &amp; design
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-cogBrand tracking-tight leading-[1.15] font-display">
           Products & Production Facilities
            </h2>
          </div>

          <div className="flex items-center gap-6 shrink-0">
           <Link
  to="/products" 
  className="group inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-cogBrand transition-colors duration-300 hover:text-cogCyan relative pb-1 after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-cogBrand/20 hover:after:bg-cogCyan after:transition-colors after:duration-300"
>
  <span>View full catalog</span>
  <ArrowRight className="w-4 h-4 transition-transform duration-300 ease-out group-hover:translate-x-1" />
</Link>
            {/* Nav Arrows */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                aria-label="Previous slide"
                className="custom-swiper-prev w-10 h-10 bg-white text-cogBrand rounded-sm border border-slate-200 flex items-center justify-center hover:border-cogCyan hover:text-cogCyan transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                aria-label="Next slide"
                className="custom-swiper-next w-10 h-10 bg-white text-cogBrand rounded-sm border border-slate-200 flex items-center justify-center hover:border-cogCyan hover:text-cogCyan transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Swiper Carousel */}
        <div className="relative">
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            spaceBetween={24}
            slidesPerView={1}
            autoplay={{
              delay: 3500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{
              clickable: true,
              el: '.custom-swiper-pagination',
            }}
            navigation={{
              nextEl: '.custom-swiper-next',
              prevEl: '.custom-swiper-prev',
            }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-4"
          >
            {productsData.map((item) => (
              <SwiperSlide key={item.id} className="h-auto">
                <ProductCard
                  img={item.img}
                  alt={item.alt}
                  title={item.title}
                  onViewClick={() => openLightbox(item.img, item.alt, item.title)}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Mobile Nav Arrows */}
          <div className="flex sm:hidden justify-center items-center gap-3 mt-6">
            <button
              aria-label="Previous slide"
              className="custom-swiper-prev w-10 h-10 bg-white text-cogBrand rounded-sm border border-slate-200 flex items-center justify-center active:border-cogCyan active:text-cogCyan transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              aria-label="Next slide"
              className="custom-swiper-next w-10 h-10 bg-white text-cogBrand rounded-sm border border-slate-200 flex items-center justify-center active:border-cogCyan active:text-cogCyan transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Pagination Container */}
          <div className="custom-swiper-pagination flex justify-center gap-2 mt-8" />
        </div>
      </div>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-md"
          onClick={closeLightbox}
        >
          {/* Top Control Bar */}
          <div className="absolute top-4 right-4 flex items-center gap-3 z-50">
            <button
              onClick={(e) => { e.stopPropagation(); setZoomScale(prev => Math.min(prev + 0.25, 3)); }}
              aria-label="Zoom in"
              className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-sm transition-colors backdrop-blur-sm"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomScale(prev => Math.max(prev - 0.25, 1)); }}
              aria-label="Zoom out"
              className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-sm transition-colors backdrop-blur-sm"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <button
              onClick={closeLightbox}
              aria-label="Close"
              className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-sm transition-colors backdrop-blur-sm"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image Container */}
          <div className="overflow-auto max-w-full max-h-full flex items-center justify-center select-none">
            <img
              src={activeImage.src}
              alt={activeImage.alt}
              style={{ transform: `scale(${zoomScale})` }}
              className="max-w-[90vw] max-h-[80vh] object-contain transition-transform duration-200 ease-out shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Caption displays Headline at full view */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center text-white bg-black/70 px-6 py-3 rounded-sm max-w-xl pointer-events-none border border-white/10">
            <h4 className="font-bold text-sm sm:text-base mb-0.5">{activeImage.title}</h4>
            <p className="text-xs text-gray-400">{activeImage.alt}</p>
          </div>
        </div>
      )}
    </section>
  );
}

export default ProductsAndDesigns;